<!-- Cash flows and income: deposits/withdrawals by month and year, costs, dividends and interest. -->
<script lang="ts">
  import * as E from "../core/engine";
  import { app, need } from "../lib/state.svelte";
  import { monthLabel } from "../lib/format";
  import { baseOption, categoryAxis, compactMoney, palette, series, tipMoney, valueAxis, type TipParam } from "../lib/chart";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";

  const m = $derived(need(app.model));
  const S = $derived(new Set(app.accList));
  const flows = $derived(m.txs.filter((t) => S.has(t.account) && (t.type === "DEPOSIT" || t.type === "WITHDRAWAL")));
  const byYear = $derived.by(() => {
    const out: Record<string, { dep: number; wd: number; n: number }> = {};
    for (const t of flows) {
      const y = t.day.slice(0, 4);
      const v = t.amt * m.fx(t.currency, m.idx(t.day));
      const o = (out[y] ||= { dep: 0, wd: 0, n: 0 });
      if (v >= 0) o.dep += v; else o.wd -= v;
      o.n++;
    }
    return out;
  });
  const feeTotal = $derived.by(() => {
    // sum within years, then across years (same summation order as in the old view)
    const byY: Record<number, number> = {};
    for (const f of m.fees) if (S.has(f.account)) byY[f.year] = (byY[f.year] || 0) + f.v;
    return Object.values(byY).reduce((s, v) => s + v, 0);
  });
  const income = $derived(m.incomes.filter((x) => S.has(x.account)));
  const incTotal = $derived(income.reduce((s, x) => s + x.gross, 0));
  const incRows = $derived.by(() => {
    const incBy: Record<number, { y: number; div: number; bond: number; cash: number; wh: number }> = {};
    for (const x of income) {
      const o = (incBy[x.year] ||= { y: x.year, div: 0, bond: 0, cash: 0, wh: 0 });
      if (x.kind === "DIVIDEND") o.div += x.gross; else if (x.symbol && m.instr[x.symbol]?.assetClass === "BOND") o.bond += x.gross; else o.cash += x.gross;
      o.wh += x.withheld;
    }
    return Object.values(incBy).sort((a, b) => b.y - a.y);
  });
  const yearRows = $derived(Object.entries(byYear).sort().reverse());
  const depTotal = $derived(Object.values(byYear).reduce((s, o) => s + o.dep, 0));
  const wdTotal = $derived(Object.values(byYear).reduce((s, o) => s + o.wd, 0));

  const flowsOpt = $derived.by(() => {
    const p = palette();
    const byMonth: Record<string, number> = {};
    for (const t of flows) { const k = t.day.slice(0, 7); byMonth[k] = (byMonth[k] || 0) + t.amt * m.fx(t.currency, m.idx(t.day)); }
    const months: string[] = [];
    if (flows.length) { let d = flows[0].day.slice(0, 7) + "-01"; while (d.slice(0, 7) <= m.today.slice(0, 7)) { months.push(d.slice(0, 7)); d = E.addDays(d, 32).slice(0, 7) + "-01"; } }
    return {
      ...baseOption(p), legend: { show: false },
      tooltip: { ...baseOption(p).tooltip, trigger: "item", formatter: (x: TipParam) => `${x.name}<br><b>${tipMoney(x.value)}</b>` },
      xAxis: categoryAxis(months, p, { formatter: monthLabel }),
      yAxis: valueAxis(compactMoney, p),
      series: [{ type: "bar", barMaxWidth: 16, data: months.map((k) => { const v = byMonth[k] || 0; return { value: v, itemStyle: { color: v >= 0 ? series(0) : p.loss, borderRadius: v >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4] } }; }) }],
    };
  });
</script>

<ViewHead title="Wpłaty i dochody" />
<div class="kpis">
  <Kpi label="Wpłaty łącznie" sub="{flows.filter((t) => t.amt > 0).length} wpłat">{#snippet value()}<Money v={depTotal} />{/snippet}</Kpi>
  <Kpi label="Wypłaty łącznie">{#snippet value()}<Money v={wdTotal} />{/snippet}</Kpi>
  <Kpi label="Koszty (prowizje i opłaty)">
    {#snippet value()}<Money v={feeTotal} d={2} />{/snippet}
    {#snippet sub()}szczegóły w zakładce <a href="#/costs">Koszty</a>{/snippet}
  </Kpi>
  <Kpi label="Dywidendy i odsetki (brutto)" sub="{income.length} wpłat">{#snippet value()}<Money v={incTotal} d={2} />{/snippet}</Kpi>
</div>
<Card title="Wpłaty netto w miesiącach" hint="wpłaty minus wypłaty (PLN po kursie NBP z dnia)"><Chart id="chFlows" option={flowsOpt} /></Card>
<div class="grid g2">
  <Card title="Wpłaty i wypłaty wg lat">
    <div class="table-wrap"><table><thead><tr><th class="l">Rok</th><th>Wpłaty</th><th>Wypłaty</th><th>Netto</th><th>Operacji</th></tr></thead><tbody>
      {#each yearRows as [y, o] (y)}
        <tr><td class="l"><b>{y}</b></td><td><Money v={o.dep} /></td><td><Money v={o.wd} /></td><td><Signed v={o.dep - o.wd} /></td><td>{o.n}</td></tr>
      {/each}
    </tbody></table></div>
  </Card>
  <Card title="Dochody wg lat (brutto)">
    <div class="table-wrap"><table><thead><tr><th class="l">Rok</th><th>Dywidendy</th><th>Kupony i odsetki z obligacji</th><th>Odsetki od gotówki</th><th>Pobrany podatek</th><th>Razem</th></tr></thead><tbody>
      {#each incRows as o (o.y)}
        <tr><td class="l"><b>{o.y}</b></td><td><Money v={o.div} d={2} /></td><td><Money v={o.bond} d={2} /></td><td><Money v={o.cash} d={2} /></td><td><Money v={o.wh} d={2} /></td><td><b><Money v={o.div + o.bond + o.cash} d={2} /></b></td></tr>
      {:else}
        <tr><td class="l muted" colspan="6">Brak dochodów.</td></tr>
      {/each}
    </tbody></table></div>
  </Card>
</div>
